import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Input,
  Spinner
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import axiosInstance from "utils/axiosInstance";

// Select and DND
import Select, { components } from 'react-select';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

// Custom CSS
import "./InfoTopicEditor.css";

// Common Components
import ThumbnailUploader from './components/ThumbnailUploader';
import StatusSelect from "./components/StatusSelect";
import HorizontalLoader from 'components/Common/HorizontalLoader';

// Custom Hooks and utils
import { useThumbnail } from 'utils/thumbnails/useThumbnail';
import { fetchThumbnailsWithCache } from "utils/thumbnails/fetchThumbnailsWithCache";
import { useEditableTopicFields } from './utils/useEditableTopicFields';

// Placeholders
import placeholderSmall from "assets/images/info/card-placeholder-small.png";


/*----------------------
/* Main Component
/*----------------------*/

const InfoTopicEditor = ({ t }) => {

  const navigate = useNavigate();
  const { infoTopicId } = useParams();

  //--------------------------------------------------
  // States
  //--------------------------------------------------
  const [openSelectId, setOpenSelectId] = useState(null);

  const [infoTopic, setInfoTopic] = useState(null);
  const [infoQuestionSets, setInfoQuestionSets] = useState([]);
  const [infoTopicLoading, setInfoTopicLoading] = useState(true);
  const [error, setError] = useState(null);

  const [questionSetStats, setQuestionSetStats] = useState({});


  //--------------------------------------------------
  // Fetch on mount
  //--------------------------------------------------

  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const [sortedQuestionSets, setSortedQuestionSets] = useState([]);
  const [firstFetching, setFirstFetching] = useState(false);

  const fetchTopicAndQuestionSets = async () => {
    try {
      setInfoTopicLoading(true);
      if(sortedQuestionSets.length===0){ setFirstFetching((prev) => true); }
      setError(null);

      // Load topic and question sets in parallel
      const [topicResponse, questionSetsResponse] = await Promise.all([
        axiosInstance.get(`/knovia/infoTopic/${infoTopicId}`),
        axiosInstance.get(`/knovia/infoTopic/${infoTopicId}/getInfoQuestionSets`)
      ]);

      console.log('infoTopic Loaded:', topicResponse.data);
      console.log('infoQuestionSets in Topic Loaded:', questionSetsResponse.data);

      setInfoTopic(topicResponse.data);

      const sorted = [...questionSetsResponse.data].sort((a, b) => a.order - b.order);
      setSortedQuestionSets(sorted);
      setInfoQuestionSets(sorted);

      // Initial Saved Status
      const initialSavedStatus = {};
      sorted.forEach(qs => {
        initialSavedStatus[qs._id] = true;
      });
      setIsSavedStatus(initialSavedStatus);

    } catch (err) {
      console.error(err);
      setError("Failed to load topic or question sets.");
    } finally {
      setInfoTopicLoading(false);
      setFirstFetching((prev) => false);
    }
  };

  const fetchCompletionStats = async (questionSets) => {
    try {
      const statsPromises = questionSets.map(async (qs) => {
        try {
          const res = await axiosInstance.get(`/knovia/infoQuestionSet/${qs._id}/getCompletionStats`);
          return { id: qs._id, stats: res.data };
        } catch (err) {
          console.error(`Failed to fetch stats for ${qs._id}`, err);
          return { id: qs._id, stats: { total: 0, completed: 0 } }; // fallback
        }
      });
      const allStats = await Promise.all(statsPromises);
      const statsMap = {};
      allStats.forEach(({ id, stats }) => {
        statsMap[id] = stats;
      });
      setQuestionSetStats(statsMap);
    } catch (err) {
      console.error(err);
      setError("Failed to load question set completion stats.");
    }
  };

  const fetchThumbnails = async (questionSets) => {
    try {
      const thumbnailMap = await fetchThumbnailsWithCache(
        questionSets,
        "infoQuestionSet",
        "sizeSmall",
        ({ id, url }) => {
          setThumbnailUrls(prev => ({
            ...prev,
            [id]: url
          }));
        },
      );
    } catch (err) {
      console.error("Thumbnail fetch failed:", err);
    }
  };

  useEffect(() => {
    fetchTopicAndQuestionSets();
  }, [infoTopicId]);

  useEffect(() => {
    if (sortedQuestionSets.length > 0) {
      // Slight delay to allow UI render first
      setTimeout(() => {
        fetchCompletionStats(sortedQuestionSets);
        fetchThumbnails(sortedQuestionSets);
      }, 0);
    }
  }, [sortedQuestionSets]);

  


  //--------------------------------------------------
  // Editing Topic fields
  //--------------------------------------------------

  const {
    isSaving: isSavingLabel,
    isSaved: isSavedLabel,
    isError: isSavedLabelError,
    handleChange: handleTopicLabelChange,
    handleSave: handleSaveLabel
  } = useEditableTopicFields(infoTopic?.label, 'editLabel', 'label', infoTopicId, axiosInstance, setInfoTopic);

  const {
    isSaving: isSavingDescription,
    isSaved: isSavedDescription,
    isError: isSavedDescriptionError,
    handleChange: handleTopicDescriptionChange,
    handleSave: handleSaveDescription
  } = useEditableTopicFields(infoTopic?.description, 'editDescription', 'description', infoTopicId, axiosInstance, setInfoTopic);



  //--------------------------------------------------
  // ADD/DELETE QuestionSets
  //--------------------------------------------------

  const handleAddQuestionSet = async () => {
    try {

      const newQuestionSetPayload = {
        topic: infoTopicId,
        label: "New question Set"
      };

      const res = await axiosInstance.post("/knovia/infoQuestionSet", newQuestionSetPayload);
      setInfoQuestionSets((prev) => [...prev, res.data]);

    } catch (err) {
      console.error(err);
      setError("Failed to add question set.");
    }
  };

  const handleDeleteQuestionSet = async (questionSetId) => {
    try {
      const deletedSet = infoQuestionSets.find((q) => q._id === questionSetId);
      if (!deletedSet) return;

      const deletedOrder = deletedSet.order;

      await axiosInstance.delete(`/knovia/infoQuestionSet/${questionSetId}`);

      setInfoQuestionSets((prev) => {
        const filtered = prev.filter((q) => q._id !== questionSetId);
        return filtered.map((q) =>
          q.order > deletedOrder ? { ...q, order: q.order - 1 } : q
        );
      });
    } catch (err) {
      console.error(err);
      setError("Failed to delete question set.");
    }
  };


  //--------------------------------------------------
  // Status
  //--------------------------------------------------

  const [isSavingStatus, setIsSavingStatus] = useState({});
  const [isSavedStatus, setIsSavedStatus] = useState({});

  const statusOptions = [
    { value: 'needs_edit', label: '待編輯' },
    { value: 'in_progress', label: '編輯中' },
    { value: 'finalized', label: '已完成' },
  ];

  const handleStatusChange = async (questionSetId, newStatus) => {
    // Set saving to true for this specific question
    setIsSavingStatus(prev => ({ ...prev, [questionSetId]: true }));
    setIsSavedStatus(prev => ({ ...prev, [questionSetId]: false }));

    try {
      const res = await axiosInstance.patch(`/knovia/infoQuestionSet/${questionSetId}/updateStatus`, {
        status: newStatus
      });

      const updatedBy = {
        name: res.data.lastModifiedBy?.username || 'Unknown',
        email: res.data.lastModifiedBy?.email || 'Unknown'
      };

      // Update local infoQuestions with the new status
      setInfoQuestionSets(prev =>
        prev.map(qs =>
          qs._id === questionSetId
            ? {
                ...qs,
                status: newStatus,
                lastStatusUpdate: {
                  updatedAt: new Date(res.data.lastUpdatedAt),
                  updatedBy
                }
              }
            : qs
        )
      );


      // Mark as saved
      setIsSavedStatus(prev => ({ ...prev, [questionSetId]: true }));

    } catch (err) {
      console.error('Failed to update status:', err);
      setError("Failed to update questionSet status.");
    } finally {
      // Clear saving status
      setIsSavingStatus(prev => ({ ...prev, [questionSetId]: false }));
    }
  };


  //--------------------------------------------------
  // QuestionSet List DND
  //--------------------------------------------------

  const [isSavingQuestionSetOrder, setIsSavingQuestionSetOrder] = useState(false);
  const [isSavedQuestionSetOrder, setIsSavedQuestionSetOrder] = useState(true);
  const [isSavedQuestionSetOrderError, setIsSavedQuestionSetOrderError] = useState(false);

  // Fires when dropping dragged items
  const handleDragEnd = (result) => {

    setIsSavedQuestionSetOrderError(false);
    setIsSavedQuestionSetOrder(false);
    // If there's no valid destination (e.g. dropped outside), do nothing
    if (!result.destination) return;

    // Reorder the array in state
    const newOrder = Array.from(infoQuestionSets);
    // extract the moved item
    const [movedItem] = newOrder.splice(result.source.index, 1);
    // add movedItem to result.destination.index
    newOrder.splice(result.destination.index, 0, movedItem);

    setInfoQuestionSets(newOrder);
    handleSaveQuestionSetOrder(newOrder);
  };

  // Saving new order
  const handleSaveQuestionSetOrder = async (newOrder) => {
    try {

      const payload = newOrder.map((q, index) => ({
        _id: q._id,
        label: q.label,
        order: index + 1,
      }));
      console.log('Saving question set order:', payload);
      setIsSavingQuestionSetOrder(true);

      await axiosInstance.patch(
        `/knovia/infoTopic/${infoTopicId}/reorderQuestionSets`,
        payload
      );

      // Update local state after successful save
      const reorderedQuestionSets = payload.map(({ _id, order }) => {
        const matchingQuestionSet = infoQuestionSets.find(q => q._id === _id);
        return { ...matchingQuestionSet, order };
      });

      setInfoQuestionSets(reorderedQuestionSets.sort((a, b) => a.order - b.order));

      //alert("QuestionSet order saved!");
      setIsSavingQuestionSetOrder(false);
      setIsSavedQuestionSetOrder(true);

    } catch (err) {
      console.error(err);
      setIsSavingQuestionSetOrder(false);
      setIsSavedQuestionSetOrderError(true);
      setError("Failed to save question set order.");
    }
  };


  //--------------------------------------------------
  // Thumbnails
  //--------------------------------------------------

  const original = useThumbnail("infoTopic", "uploadThumbnail", "viewThumbnail");
  const medium   = useThumbnail("infoTopic", "uploadThumbnail/sizeMedium", "viewThumbnail/sizeMedium");
  const small    = useThumbnail("infoTopic", "uploadThumbnail/sizeSmall", "viewThumbnail/sizeSmall");

  const handleFileChange = (e, uploadFn, size) => {
    const file = e.target.files[0];
    if (file) {
      uploadFn(file, infoTopic, axiosInstance, size);
    }
  };

  useEffect(() => {
    if (infoTopic?._id) {
      original.fetchThumbnail(infoTopic, axiosInstance, "sizeOriginal");
      medium.fetchThumbnail(infoTopic, axiosInstance, "sizeMedium");
      small.fetchThumbnail(infoTopic, axiosInstance, "sizeSmall");
    }
  }, [infoTopic?._id]);




  //--------------------------------------------------
  // Render Logic (Not in use)
  //--------------------------------------------------
  /*
  if (infoTopicLoading) return <div>Loading...</div>;
  if (error) return <div style={{ color: "red" }}>{error}</div>;
  if (!infoTopic) return <div>No InfoTopic found.</div>;
  */


  //--------------------------------------------------
  // Render
  //--------------------------------------------------
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody>

              {/* Title and Button row */}
              <Row>
                <Col xs={4}>
                  <CardTitle className="mb-4">{t("編輯主題")}</CardTitle>
                </Col>
                <Col xs={8} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                  <Button color="info" style={{ padding: "7px 8px", marginRight: "0.3rem" }}
                    onClick={(e) => { navigate(`/editInfoCollections/${infoTopic.infoCollection}`); }}
                  >
                    <i className='bx bx-arrow-back' ></i>{` 回到主題集`}
                  </Button>
                  <Button color="primary" style={{ marginRight: "0.3rem" }}>
                    複製主題
                  </Button>
                </Col>
              </Row>


              {/* Edit Topic Info */}
              <Row style={{ padding: "1rem" }}>
                <Col xs={12}>

                  {/* Topic Label Field */}
                  <Row className="mb-3 mb-md-2">

                    <Col xs={12} md={4} xl={3} className="d-flex align-items-center mb-2 mb-md-0">
                      <h4 className="mb-0 me-3">{t("主題名稱:", { defaultValue: "主題名稱:" })}</h4>
                      {isSavingLabel && <span style={{ color: "#f1b34c" }}>Saving...</span>}
                      {!isSavingLabel && !isSavedLabelError &&
                        <span style={{ color: isSavedLabel ? "#34c38f" : "#aeabab" }}>✔ Saved</span>
                      }
                      {!isSavingLabel && isSavedLabelError &&
                        <span style={{ color: "red" }}>Error Saving Label!</span>
                      }
                    </Col>

                    <Col xs={8} md={6} xl={4} className="d-flex">
                      <Input className="me-3"
                        type="text"
                        value={infoTopic?.label || ""}
                        onChange={handleTopicLabelChange}
                      />
                      <Button color="outline-success" onClick={()=>{handleSaveLabel(infoTopic?.label)}}
                        style={{ padding: "7px 8px" }}
                      ><i className="bx bx-check" /></Button>
                    </Col>
                    
                  </Row>

                  {/* Topic Description Field */}
                  <Row className="mb-3 mb-md-2">

                    <Col xs={12} md={4} xl={3} className="d-flex align-items-center mb-2 mb-md-0">
                      <h4 className="mb-0 me-3">{t("主題描述:", { defaultValue: "主題描述:" })}</h4>
                      {isSavingDescription && <span style={{ color: "#f1b34c" }}>Saving...</span>}
                      {!isSavingDescription && !isSavedDescriptionError &&
                        <span style={{ color: isSavedDescription ? "#34c38f" : "#aeabab" }}>✔ Saved</span>
                      }
                      {!isSavingDescription && isSavedDescriptionError &&
                        <span style={{ color: "red" }}>Error Saving Label!</span>
                      }
                    </Col>

                    <Col xs={8} md={6} xl={4} className="d-flex">
                      <Input className="me-3"
                        type="text"
                        value={infoTopic?.description || ""}
                        onChange={handleTopicDescriptionChange}
                      />
                      <Button color="outline-success" onClick={()=>{handleSaveDescription(infoTopic?.description)}}
                        style={{ padding: "7px 8px" }}
                      ><i className="bx bx-check" /></Button>
                    </Col>

                  </Row>


                  {/* Topic thumbnails */}
                  <Row>
                    <Col xs={12}>
                      <h4>封面圖片:</h4>
                    </Col>
                  </Row>

                  <ThumbnailUploader
                    original={original}
                    medium={medium}
                    small={small}
                    handleFileChange={handleFileChange}
                  />


                  {/* Horizontal */}
                  <div className="d-flex d-md-none"
                    style={{
                      borderTop: "1px solid #e8e8e8",
                      margin: "30px 0 30px -36px",
                      width: "calc(100% + 72px)",
                    }}
                  />


                  {/* Question Set Edit */}

                  <Row style={{ marginBottom: "0.5rem" }}>
                    <Col xs={12} style={{ display: "flex", justifyContent: "space-between", padding: "0" }}>

                      <Row style={{ width: "100%", margin: "0" }}>
                        <Col xs={12} md={4}>
                          <h3>主題中的題組</h3>
                          {isSavingQuestionSetOrder && <span style={{ color: "#f1b34c" }}>Saving Order...</span>}
                          {!isSavingQuestionSetOrder && !isSavedQuestionSetOrderError &&
                            <span style={{ color: isSavedQuestionSetOrder ? "#34c38f" : "#aeabab" }}>✔ Saved Order</span>
                          }
                          {!isSavingQuestionSetOrder && isSavedQuestionSetOrderError &&
                            <span style={{ color: "red" }}>Error Saving Question Set Order!</span>
                          }
                        </Col>
                        <Col xs={12} md={8} style={{ display:"flex", alignItems:"center", justifyContent:"flex-end" }}>
                          <Button color="outline-info" onClick={handleAddQuestionSet} style={{ marginRight: "1rem" }}>
                            + 新增題組
                          </Button>
                          {/*
                          <Button color="outline-warning" onClick={()=>{}} style={{ marginRight: "1rem" }}>
                            複製題組到其他主題
                          </Button>
                          */}
                        </Col>
                      </Row>

                    </Col>
                  </Row>


                  {/* Question Set Drag List */}
                  <Row>
                    <Col xs={12} style={{ padding: "0.5rem 3rem", border: "1px solid #bcbbbb", borderRadius: "5px", minHeight: "13rem" }}>
                      {infoQuestionSets.length === 0 ? (
                        firstFetching ? ( 
                          <HorizontalLoader />
                        ) : (
                          <div className="d-flex flex-column" style={{ gap: "10px", padding: "10px 0px " }}>
                            {[1,2].map((order, index) => {
                              return (
                                <div key={index} style={{
                                  width: "70%",
                                  height: "70px",
                                  border: "1px dashed #3d8dd5",
                                  borderRadius: "5px",
                                }} />
                              )
                            })}
                          </div>
                        )
                      ) : (
                        <DragDropContext onDragEnd={handleDragEnd}>
                          <Droppable droppableId="questionSetList">
                            {(provided) => (
                              <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                              >
                                {infoQuestionSets.map((q, index) => (

                                  <Draggable key={q._id} draggableId={q._id} index={index}>
                                    {(provided) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                      >
                                        {/* Single row content */}
                                        <Row key={q._id} className="draggable-row mt-3 mb-4 mb-mt-0 mb-md-0">

                                          <Col xs={12} md={8} style={{ display: "flex", alignItems: "center", padding: "0.25rem 0rem" }}>
                                            
                                            <Row style={{ width: "100%", height: "100%", margin: "0", border: "1px solid #bcbbbb", borderRadius: "5px" }}>
                                              <Col xs={12} style={{ padding: 0 }}>
                                                <div style={{ position: "relative", width: "100%", height: "100%", minHeight: "3rem", display: "flex", alignItems: "center" }}>
                                                  {/* Background Bar */}
                                                  <div style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: "100%",
                                                    height: "100%",
                                                    backgroundColor: "#f0f0f0",  // base gray
                                                    borderRadius: "5px",
                                                    zIndex: 0,
                                                  }} />

                                                  {/* Progress Bar */}
                                                  <div style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: `${questionSetStats[q._id] ? (questionSetStats[q._id].completed / questionSetStats[q._id].total) * 100 : 0}%`,
                                                    height: "100%",
                                                    backgroundColor: "#d3eaff", // blue highlight
                                                    borderRadius: "5px 0 0 5px",
                                                    zIndex: 1,
                                                    transition: "width 1.2s ease-in-out"
                                                  }} />

                                                  {/* Text content */}
                                                  <div style={{
                                                    position: "relative",
                                                    zIndex: 2,
                                                    padding: "0.5rem 1rem",
                                                    width: "100%",
                                                  }}>
                                                    <p style={{ margin: 0, fontWeight: 500 }}>
                                                      {`${q.order}. ${q.label}`}{" "}
                                                      {questionSetStats[q._id] && (
                                                        <span style={{ fontSize: "0.9rem", fontWeight: 400 }}>
                                                          （{questionSetStats[q._id].completed}/{questionSetStats[q._id].total} 問題已完成編輯）
                                                        </span>
                                                      )}
                                                    </p>
                                                    {q.lastStatusUpdate && (
                                                      <div style={{ fontSize: "0.75rem", color: "#666", marginTop: "0.2rem", lineBreak: 'anywhere' }}>
                                                        最近狀態由 <strong>{q.lastStatusUpdate.updatedBy.name}</strong>（{q.lastStatusUpdate.updatedBy.email}）<br />
                                                        於 {new Date(q.lastStatusUpdate.updatedAt).toLocaleString()} 更新
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              </Col>
                                            </Row>

                                          </Col>


                                          {/* Edit and Delete Question Sets */}
                                          <Col xs={4} sm={6} md={2} 
                                            style={{ display: "flex", alignItems: "center", justifyContent: "center"}}
                                          >
                                            <Button color="outline-info" style={{ padding: "7px 8px", marginRight: "0.3rem" }}
                                              onClick={(e) => { navigate(`/editInfoQuestionSets/${q._id}`); }}
                                            >
                                              <i className="bx bxs-pencil" />
                                            </Button>

                                            <Button color="outline-danger" onClick={() => handleDeleteQuestionSet(q._id)}>
                                              -
                                            </Button>
                                          </Col>

                                          {/* Question Sets Edit Status */}
                                          <Col xs={8} sm={6} md={2}
                                            style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}
                                          >
                                            <div className="w-100 px-1 px-md-0">
                                              <StatusSelect
                                                value={q.status}
                                                onChange={(newStatus) => handleStatusChange(q._id, newStatus)}
                                                options={statusOptions}
                                                selectId={q._id}
                                                openSelectId={openSelectId}
                                                setOpenSelectId={setOpenSelectId}
                                              />
                                            </div>

                                            <div className="w-100 px-1 px-md-0">
                                              {isSavingStatus[q._id] && <span style={{ color: "#f1b34c" }}>Saving...</span>}
                                              {!isSavingStatus[q._id] && (
                                                <span style={{ color: isSavedStatus[q._id] ? "#34c38f" : "#aeabab" }}>
                                                  ✔ Saved!
                                                </span>
                                              )}
                                            </div>

                                          </Col>

                                        </Row>
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </DragDropContext>
                      )}
                    </Col>
                  </Row>


                </Col>
              </Row>


            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
}

export default withTranslation()(InfoTopicEditor);




